import { useTranslation } from 'next-i18next';

import { ModalActions, ModalBody } from '@components/ui';
import { NUMBER_PREFIX_RO } from '@helpers/PhoneNumberHelpers';
import { useConfig } from '@hooks/data/useConfig';

import PhoneNumberForm from './PhoneNumberForm';
import PhoneVerificationHelp from './PhoneVerificationHelp';

import styles from './PhoneVerificationModalNumberStep.module.scss';

interface Props {
  onClose: () => void,
  onSubmitSuccess: (number: string) => void,
  phoneNumber?: string,
  isEdit?: boolean,
  isTimerRunning: boolean,
  timerTotalSeconds: number
}

const PhoneVerificationModalNumberStep = (props: Props) => {

  const {
    onClose,
    onSubmitSuccess,
    phoneNumber,
    isEdit,
    isTimerRunning,
    timerTotalSeconds,
  } = props;

  const { t } = useTranslation();

  const { data: config } = useConfig();
  const { verifyPhoneNumber } = config || {};
  const {
    header: headerCopy = {
      validate: t('PHONE_VALIDATION.FORM_STEP.TITLE_NEW'),
      modify: t('PHONE_VALIDATION.FORM_STEP.TITLE_EXISTING')
    },
    button: buttonCopy = {
      send: t('PHONE_VALIDATION.FORM_STEP.PRIMARY_BUTTON')
    },
    body: bodyCopy = {
      validate: t('PHONE_VALIDATION.FORM_STEP.SUBTITLE')
    },
    textBox: textBoxCopy = {
      label: t('PHONE_VALIDATION.FORM_STEP.FORM_TITLE'),
      placeholder: t('PHONE_VALIDATION.FORM_STEP.NUMBER_PLACEHOLDER')
    },
    postAction: postActionCopy = {
      validateLater: t('PHONE_VALIDATION.FORM_STEP.SECONDARY_BUTTON')
    }
  } = verifyPhoneNumber || {};

  const primaryButtonText = isTimerRunning ? `${buttonCopy.send} ${t('LABELS.IN_LOWERCASE')} ${timerTotalSeconds}` : buttonCopy.send;

  return (
    <ModalBody
      className={styles.root}
      childrenClassName={styles.content}
      headerProps={{
        className: styles.header,
        titleClassName: styles.title,
        title: isEdit ? headerCopy.modify : headerCopy.validate,
        desktopProps: {
          size: 'large',
          withCloseButton: true
        },
        responsiveProps: {
          size: 'medium',
          withCloseButton: true
        },
        onClose,
      }}
    >
      <div className={styles.subtitle}>{bodyCopy.validate}</div>
      <PhoneNumberForm
        initialValues={{ phoneNumber, prefix: NUMBER_PREFIX_RO }}
        title={textBoxCopy?.label}
        className={styles.form}
        onSubmitSuccess={(number: string) => {
          onSubmitSuccess(number);
        }}
        renderBeforeActions={() => {
          return (
            <PhoneVerificationHelp className={styles.help} />
          );
        }}
        renderActions={(formikProps) => {
          const {
            handleSubmit, isSubmitting, isValid, values
          } = formikProps;
          return (
            <ModalActions
              loading={isSubmitting}
              withBorder={false}
              withPadding={false}
              className={styles.actions}
              primary={{
                text: primaryButtonText,
                disabled: isSubmitting || !isValid || isTimerRunning || !values?.phoneNumber,
                loading: isSubmitting,
                onClick: () => {
                  handleSubmit();
                }
              }}
              secondary={{
                text: postActionCopy.validateLater,
                disabled: isSubmitting,
                onClick: () => {
                  onClose();
                }
              }}
            />
          );
        }}
      />
    </ModalBody>
  );
};

export default PhoneVerificationModalNumberStep;
